
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';

const bg = '#f0ebe3';
const red = '#6B1A1A';
const dark = '#4a1010';
const serif = 'Playfair Display, serif';
const body = 'Cormorant Garamond, serif';
const API = 'https://api.hermespartnercapital.co';

const statusColors = {
  sent: { bg: '#fff3cd', color: '#856404' },
  signed: { bg: '#d4edda', color: '#155724' },
  paid: { bg: '#d1ecf1', color: '#0c5460' },
  onboarding: { bg: '#e2d9f3', color: '#4a2c7a' },
  live: { bg: '#d4edda', color: '#155724' },
  scheduled: { bg: '#fff3cd', color: '#856404' },
  completed: { bg: '#d4edda', color: '#155724' },
  cancelled: { bg: '#f8d7da', color: '#721c24' },
  'no-show': { bg: '#f8d7da', color: '#721c24' },
};

function Admin() {
  const [activeTab, setActiveTab] = useState('contracts');
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  // ── Contracts state ──
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(true);

  // ── Meetings state ──
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    businessName: '', contactName: '', phone: '', email: '',
    meetingDate: '', meetingTime: '', meetingType: 'Demo', rep: 'Randy', notes: ''
  });
  const [meetingStatus, setMeetingStatus] = useState('');
  const [submittingMeeting, setSubmittingMeeting] = useState(false);

  // ── Leaderboard state ──
  const [leaderboard, setLeaderboard] = useState([]);

  // ── Announcements state ──
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [postingAnnouncement, setPostingAnnouncement] = useState(false);

  // ── Team state ──
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({ full_name: '', email: '', password: '', role: 'appointment_setter' });

  // ── Onboarding Panel state ──
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState({
    business_name: '',
    contract_id: '',
    hours_weekday: '',
    hours_weekend: '',
    services: '',
    top_services: '',
    no_services: '',
    free_estimates: '',
    emergency_handling: '',
    transfer_number: '',
    pricing_policy: '',
    greeting_name: '',
    ai_name: '',
    tone: '',
    special_instructions: '',
    vapi_notes: '',
    ghl_email: '',
    client_email: '',
    fallback: '',
    sms_alert_number: '',
    phone_provider: '',
    pipeline: '',
    ghl_tags: '',
    vapi_built: false,
    vapi_tested: false,
    phone_forwarded: false,
    ghl_created: false,
    ghl_connected: false,
    portal_created: false,
    test_call: false,
    client_notified: false,
  });
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [sendingOnboarding, setSendingOnboarding] = useState(false);

  useEffect(() => {
    checkAdmin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'contracts') fetchContracts();
    if (activeTab === 'meetings') fetchMeetings();
    if (activeTab === 'leaderboard') fetchLeaderboard();
    if (activeTab === 'announcements') fetchAnnouncements();
    if (activeTab === 'team') fetchTeamMembers();
  }, [activeTab]);

  const checkAdmin = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate('/login'); return; }
    const { data: profile } = await supabase.from('profiles').select('*').eq('email', session.user.email).single();
    if (profile?.role !== 'admin') { navigate('/dashboard'); return; }
    setCurrentUser(profile);
    fetchContracts();
  };

  // ── CONTRACTS ────────────────────────────────────────────────────────────
  const fetchContracts = async () => {
    setContractsLoading(true);
    try {
      const res = await fetch(`${API}/api/contracts`);
      const data = await res.json();
      if (data.success) setContracts(data.contracts);
    } catch {}
    setContractsLoading(false);
  };

  const updateContractStatus = async (contractId, status) => {
    await fetch(`${API}/api/contracts/${contractId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchContracts();
  };

  const totalMRR = contracts.filter(c => c.status === 'live').length * 350;
  const totalSetup = contracts.filter(c => ['paid', 'onboarding', 'live'].includes(c.status)).length * 500;
  const pendingSignature = contracts.filter(c => c.status === 'sent').length;
  const activeClients = contracts.filter(c => c.status === 'live').length;

  // ── MEETINGS ─────────────────────────────────────────────────────────────
  const fetchMeetings = async () => {
    setMeetingsLoading(true);
    try {
      const res = await fetch(`${API}/api/meetings`);
      const data = await res.json();
      if (data.success) setMeetings(data.meetings);
    } catch {}
    setMeetingsLoading(false);
  };

  const handleCreateMeeting = async () => {
    if (!meetingForm.businessName || !meetingForm.contactName || !meetingForm.phone || !meetingForm.meetingDate || !meetingForm.meetingTime) {
      setMeetingStatus('Please fill in all required fields');
      return;
    }
    setSubmittingMeeting(true);
    setMeetingStatus('');
    try {
      const res = await fetch(`${API}/api/create-meeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meetingForm)
      });
      const data = await res.json();
      if (data.success) {
        setMeetingStatus('Meeting created! Check your email for SMS templates.');
        setMeetingForm({ businessName: '', contactName: '', phone: '', email: '', meetingDate: '', meetingTime: '', meetingType: 'Demo', rep: 'Randy', notes: '' });
        setShowMeetingForm(false);
        fetchMeetings();
      } else throw new Error(data.error);
    } catch (err) {
      setMeetingStatus(`Error: ${err.message}`);
    }
    setSubmittingMeeting(false);
  };

  const updateMeetingStatus = async (id, status) => {
    await fetch(`${API}/api/meetings/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    fetchMeetings();
  };

  const groupedMeetings = meetings.reduce((groups, meeting) => {
    const date = meeting.meeting_date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(meeting);
    return groups;
  }, {});

  // ── LEADERBOARD ───────────────────────────────────────────────────────────
  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/api/contracts`);
      const data = await res.json();
      if (data.success) {
        const paidContracts = data.contracts.filter(c => ['paid', 'onboarding', 'live'].includes(c.status));
        const randy = paidContracts.filter(c => c.rep === 'Randy' || !c.rep).length;
        const matt = paidContracts.filter(c => c.rep === 'Matt').length;
        setLeaderboard([
          { name: 'Randy Lepe', deals: randy, mrr: randy * 350, setup: randy * 500, role: 'Managing Partner' },
          { name: 'Matthew Moskvitin', deals: matt, mrr: matt * 350, setup: matt * 500, role: 'Managing Partner' },
        ].sort((a, b) => b.deals - a.deals));
      }
    } catch {}
  };

  // ── ANNOUNCEMENTS ─────────────────────────────────────────────────────────
  const fetchAnnouncements = async () => {
    const { data } = await supabase.from('announcements').select('*').order('created_at', { ascending: false });
    setAnnouncements(data || []);
  };

  const handlePostAnnouncement = async () => {
    if (!announcementTitle || !newAnnouncement) return;
    setPostingAnnouncement(true);
    await supabase.from('announcements').insert({
      title: announcementTitle,
      content: newAnnouncement,
      posted_by: currentUser?.full_name || 'Admin'
    });
    setAnnouncementTitle('');
    setNewAnnouncement('');
    setPostingAnnouncement(false);
    fetchAnnouncements();
  };

  // ── TEAM ──────────────────────────────────────────────────────────────────
  const fetchTeamMembers = async () => {
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: true });
    setTeamMembers(data || []);
  };

  const handleRoleChange = async (id, newRole) => {
    await supabase.from('profiles').update({ role: newRole }).eq('id', id);
    fetchTeamMembers();
  };

  const handleAddMember = async () => {
    if (!newMember.full_name || !newMember.email || !newMember.password) {
      alert('Please fill in all fields');
      return;
    }
    setAddingMember(true);
    try {
      const response = await fetch(`${API}/api/create-member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newMember.email,
          password: newMember.password,
          full_name: newMember.full_name,
          role: newMember.role
        })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      await fetchTeamMembers();
      setShowAddMember(false);
      setNewMember({ full_name: '', email: '', password: '', role: 'appointment_setter' });
      alert(`${newMember.full_name} added successfully!`);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setAddingMember(false);
  };

  // ── ONBOARDING PANEL ──────────────────────────────────────────────────────
  const updateOB = (field, value) => setOnboardingForm(prev => ({ ...prev, [field]: value }));

  const handleSendOnboarding = async () => {
    if (!onboardingForm.client_email || !onboardingForm.contract_id) {
      alert('Please fill in client email and contract ID');
      return;
    }
    setSendingOnboarding(true);
    try {
      // Save admin notes to Supabase
      await supabase.from('onboarding').insert({
        contract_id: onboardingForm.contract_id,
        business_name: onboardingForm.business_name,
        hours: { weekday: onboardingForm.hours_weekday, weekend: onboardingForm.hours_weekend },
        services: onboardingForm.services.split(',').map(s => s.trim()),
        emergency_handling: onboardingForm.emergency_handling,
        tone: onboardingForm.tone,
        transfer_number: onboardingForm.transfer_number,
        special_instructions: onboardingForm.special_instructions,
        status: 'admin_notes_saved'
      });

      // Send onboarding link to client
      await fetch(`${API}/api/send-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientEmail: onboardingForm.client_email,
          businessName: onboardingForm.business_name,
          contractId: onboardingForm.contract_id,
        })
      });

     alert(`Form sent to ${onboardingForm.client_email} — hang up and start building!`);
      setOnboardingStep(3);
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setSendingOnboarding(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const inputStyle = { backgroundColor: 'transparent', border: 'none', borderBottom: '0.5px solid #d4c9bc', padding: '10px 0', fontSize: '14px', fontFamily: body, color: '#2a1a1a', outline: 'none', width: '100%' };
  const selectStyle = { ...inputStyle, backgroundColor: 'transparent' };
  const obInput = { width: '100%', padding: '10px 0', border: 'none', borderBottom: '0.5px solid rgba(201,168,130,0.3)', backgroundColor: 'transparent', fontFamily: body, fontSize: '14px', color: '#f0ebe3', outline: 'none', boxSizing: 'border-box' };
  const obSelect = { ...obInput };
  const obLabel = { color: '#c9a882', fontSize: '10px', letterSpacing: '2px', display: 'block', marginBottom: '6px' };
  const obField = { marginBottom: '24px' };

  return (
    <div style={{ backgroundColor: bg, minHeight: '100vh', fontFamily: body, color: '#2a1a1a' }}>
      {/* NAV */}
      <nav style={{ backgroundColor: bg, borderBottom: '0.5px solid #d4c9bc', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ color: red, fontSize: '13px', letterSpacing: '4px', margin: 0 }}>🦅 HERMES PARTNER CAPITAL</p>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button onClick={() => navigate('/demo')} style={{ backgroundColor: '#28a745', color: 'white', padding: '10px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
            DEMO BUILDER
          </button>
          <button onClick={() => { setShowMeetingForm(true); setActiveTab('meetings'); }} style={{ backgroundColor: red, color: bg, padding: '10px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
            + ADD MEETING
          </button>
          <button onClick={() => setOnboardingOpen(!onboardingOpen)} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '8px 16px', fontSize: '11px', letterSpacing: '2px', fontFamily: body, cursor: 'pointer', color: '#555' }}>
            NEW CLIENT
          </button>
          <button onClick={() => navigate('/dashboard')} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '8px 20px', fontSize: '11px', letterSpacing: '2px', fontFamily: body, cursor: 'pointer', color: '#555' }}>CRM</button>
<button onClick={handleSignOut} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '8px 20px', fontSize: '11px', letterSpacing: '2px', fontFamily: body, cursor: 'pointer', color: '#555' }}>SIGN OUT</button>
        </div>
      </nav>

      {/* TABS */}
      <div style={{ borderBottom: '0.5px solid #d4c9bc', padding: '0 40px', display: 'flex', gap: '0' }}>
        {[
          { key: 'contracts', label: 'CLIENTS' },
          { key: 'meetings', label: 'MEETINGS' },
          { key: 'leaderboard', label: 'LEADERBOARD' },
          { key: 'announcements', label: 'ANNOUNCEMENTS' },
          { key: 'team', label: 'TEAM' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === tab.key ? `2px solid ${red}` : '2px solid transparent',
            padding: '16px 24px', fontSize: '11px', letterSpacing: '3px', fontFamily: body,
            cursor: 'pointer', color: activeTab === tab.key ? red : '#888', fontWeight: activeTab === tab.key ? 'bold' : 'normal'
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '40px' }}>

        {/* ── CONTRACTS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'contracts' && (
          <div>
            <h2 style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: '0 0 30px' }}>Client Pipeline</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1px', backgroundColor: '#d4c9bc', border: '1px solid #d4c9bc', marginBottom: '30px' }}>
              {[
                { label: 'MONTHLY RECURRING', value: `$${totalMRR.toLocaleString()}`, sub: 'per month' },
                { label: 'SETUP FEES COLLECTED', value: `$${totalSetup.toLocaleString()}`, sub: 'total' },
                { label: 'ACTIVE CLIENTS', value: activeClients, sub: 'live' },
                { label: 'PENDING SIGNATURE', value: pendingSignature, sub: 'awaiting' },
              ].map((stat, i) => (
                <div key={i} style={{ backgroundColor: bg, padding: '25px' }}>
                  <p style={{ color: red, fontSize: '10px', letterSpacing: '3px', margin: '0 0 8px' }}>{stat.label}</p>
                  <p style={{ fontFamily: serif, fontSize: '28px', fontWeight: '700', color: dark, margin: '0 0 4px' }}>{stat.value}</p>
                  <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>{stat.sub}</p>
                </div>
              ))}
            </div>

            {['sent', 'signed', 'paid', 'onboarding', 'live'].map(stage => {
              const stageContracts = contracts.filter(c => c.status === stage);
              if (stageContracts.length === 0) return null;
              return (
                <div key={stage} style={{ marginBottom: '25px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <span style={{ ...statusColors[stage], padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', letterSpacing: '1px' }}>
                      {stage.toUpperCase()}
                    </span>
                    <span style={{ color: '#888', fontSize: '12px' }}>{stageContracts.length} contract{stageContracts.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ border: '1px solid #d4c9bc' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', backgroundColor: dark, padding: '12px 20px' }}>
                      {['BUSINESS', 'CONTACT', 'DATE', 'CONTRACT ID', 'ACTION'].map(h => (
                        <p key={h} style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '2px', margin: 0 }}>{h}</p>
                      ))}
                    </div>
                    {stageContracts.map((c, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '14px 20px', borderTop: '0.5px solid #d4c9bc', alignItems: 'center' }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{c.prospect_info?.businessName}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>{c.prospect_info?.email}</p>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>{c.prospect_info?.primaryContact}</p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>{new Date(c.created_at).toLocaleDateString()}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>{c.id?.slice(-8)}</p>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={async () => {
                              if (c.status === 'live') {
                                // Load existing client data from crm_clients
                                const res = await fetch(`${API}/api/crm-clients`);
                                const data = await res.json();
                                const existingClient = data.clients?.find(cl => cl.owner_email === c.prospect_info?.email || cl.business_name === c.prospect_info?.businessName);
                                setOnboardingForm(prev => ({
                                  ...prev,
                                  business_name: c.prospect_info?.businessName,
                                  contract_id: c.id,
                                  client_email: c.prospect_info?.email,
                                  vapi_built: !!existingClient?.vapi_assistant_id,
                                  vapi_assistant_id: existingClient?.vapi_assistant_id || '',
                                  portal_created: !!existingClient,
                                  portal_subdomain: existingClient?.subdomain || '',
                                  client_notified: !!existingClient,
                                  is_live: true,
                                }));
                                setOnboardingStep(4);
                              } else {
                                setOnboardingForm(prev => ({ ...prev, business_name: c.prospect_info?.businessName, contract_id: c.id, client_email: c.prospect_info?.email, is_live: false }));
                                setOnboardingStep(1);
                              }
                              setOnboardingOpen(true);
                            }}
                            style={{ fontSize: '11px', padding: '4px 10px', border: '1px solid #c9a882', backgroundColor: 'transparent', color: '#c9a882', fontFamily: body, cursor: 'pointer' }}
                          >
                            {c.status === 'live' ? 'VIEW' : 'ONBOARD'}
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Delete ${c.prospect_info?.businessName}?`)) {
                                await supabase.from('contracts').delete().eq('id', c.id);
                                fetchContracts();
                              }
                            }}
                            style={{ fontSize: '11px', padding: '4px 10px', border: '1px solid #dc3545', backgroundColor: 'transparent', color: '#dc3545', fontFamily: body, cursor: 'pointer' }}
                          >
                            DELETE
                          </button>
                          <select
                            value={c.status}
                            onChange={e => updateContractStatus(c.id, e.target.value)}
                            style={{ fontSize: '11px', padding: '4px 8px', border: '1px solid #d4c9bc', backgroundColor: bg, fontFamily: body, cursor: 'pointer' }}
                          >
                            <option value="sent">Sent</option>
                            <option value="signed">Signed</option>
                            <option value="paid">Paid</option>
                            <option value="onboarding">Onboarding</option>
                            <option value="live">Live</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {contractsLoading && <p style={{ color: '#888' }}>Loading...</p>}
            {!contractsLoading && contracts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', border: '1px solid #d4c9bc' }}>
                <p style={{ color: '#888', fontStyle: 'italic' }}>No contracts yet. Run a demo to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* ── MEETINGS TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'meetings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <h2 style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: 0 }}>Meeting Calendar</h2>
              <button onClick={() => setShowMeetingForm(!showMeetingForm)} style={{ backgroundColor: red, color: bg, padding: '12px 28px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                {showMeetingForm ? 'CANCEL' : '+ CREATE MEETING'}
              </button>
            </div>

            {meetingStatus && (
              <div style={{ padding: '15px 20px', marginBottom: '20px', backgroundColor: meetingStatus.includes('Error') ? '#f8d7da' : '#d4edda', borderRadius: '6px' }}>
                <p style={{ margin: 0, color: meetingStatus.includes('Error') ? '#721c24' : '#155724', fontSize: '14px' }}>{meetingStatus}</p>
              </div>
            )}

            {showMeetingForm && (
              <div style={{ border: '1px solid #d4c9bc', padding: '30px', marginBottom: '30px', backgroundColor: bg }}>
                <p style={{ color: red, fontSize: '10px', letterSpacing: '3px', margin: '0 0 24px' }}>NEW MEETING</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>BUSINESS NAME *</p>
                    <input placeholder="e.g. Johnson Plumbing" value={meetingForm.businessName} onChange={e => setMeetingForm({...meetingForm, businessName: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>CONTACT NAME *</p>
                    <input placeholder="e.g. John Smith" value={meetingForm.contactName} onChange={e => setMeetingForm({...meetingForm, contactName: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>PHONE *</p>
                    <input placeholder="e.g. (224) 555-0123" value={meetingForm.phone} onChange={e => setMeetingForm({...meetingForm, phone: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>EMAIL</p>
                    <input placeholder="e.g. john@business.com" value={meetingForm.email} onChange={e => setMeetingForm({...meetingForm, email: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>DATE *</p>
                    <input type="date" value={meetingForm.meetingDate} onChange={e => setMeetingForm({...meetingForm, meetingDate: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>TIME *</p>
                    <input type="time" value={meetingForm.meetingTime} onChange={e => setMeetingForm({...meetingForm, meetingTime: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>MEETING TYPE</p>
                    <select value={meetingForm.meetingType} onChange={e => setMeetingForm({...meetingForm, meetingType: e.target.value})} style={selectStyle}>
                      <option>Demo</option>
                      <option>Follow-up</option>
                      <option>Close</option>
                      <option>Onboarding</option>
                      <option>Check-in</option>
                    </select>
                  </div>
                  <div>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>ASSIGNED REP</p>
                    <select value={meetingForm.rep} onChange={e => setMeetingForm({...meetingForm, rep: e.target.value})} style={selectStyle}>
                      <option value="Randy">Randy Lepe</option>
                      <option value="Matt">Matthew Moskvitin</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>NOTES</p>
                  <input placeholder="Any additional notes..." value={meetingForm.notes} onChange={e => setMeetingForm({...meetingForm, notes: e.target.value})} style={inputStyle} />
                </div>
                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>
                    After creating this meeting, you'll receive an email with 3 ready-to-send SMS templates — confirmation, day-before reminder, and 1-hour reminder. Just copy and paste to your phone.
                  </p>
                </div>
                <button onClick={handleCreateMeeting} disabled={submittingMeeting} style={{ backgroundColor: submittingMeeting ? '#ccc' : red, color: bg, padding: '14px 28px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: submittingMeeting ? 'not-allowed' : 'pointer', fontFamily: body }}>
                  {submittingMeeting ? 'CREATING...' : 'CREATE MEETING'}
                </button>
              </div>
            )}

            {meetingsLoading ? (
              <p style={{ color: '#888' }}>Loading meetings...</p>
            ) : Object.keys(groupedMeetings).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', border: '1px solid #d4c9bc' }}>
                <p style={{ color: '#888', fontStyle: 'italic' }}>No meetings scheduled. Create your first meeting above.</p>
              </div>
            ) : (
              Object.entries(groupedMeetings).map(([date, dayMeetings]) => {
                const d = new Date(date + 'T12:00:00');
                const dayLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                const isToday = new Date().toDateString() === d.toDateString();
                return (
                  <div key={date} style={{ marginBottom: '25px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px' }}>
                      <div style={{ width: '56px', height: '56px', backgroundColor: isToday ? red : dark, borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <p style={{ color: '#c9a882', fontSize: '9px', letterSpacing: '1px', margin: 0 }}>{d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</p>
                        <p style={{ color: 'white', fontFamily: serif, fontSize: '22px', fontWeight: '700', margin: 0, lineHeight: 1 }}>{d.getDate()}</p>
                      </div>
                      <div>
                        <p style={{ margin: 0, fontFamily: serif, fontSize: '18px', fontWeight: '700', color: dark }}>{dayLabel}</p>
                        {isToday && <p style={{ margin: 0, color: red, fontSize: '11px', letterSpacing: '2px' }}>TODAY</p>}
                      </div>
                    </div>
                    <div style={{ borderLeft: `2px solid ${isToday ? red : '#d4c9bc'}`, marginLeft: '27px', paddingLeft: '20px' }}>
                      {dayMeetings.map((m, i) => (
                        <div key={i} style={{ backgroundColor: 'white', border: '0.5px solid #d4c9bc', padding: '16px 20px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <p style={{ margin: 0, fontFamily: serif, fontSize: '18px', fontWeight: '700', color: dark, minWidth: '60px' }}>
                              {new Date(`2000-01-01T${m.meeting_time}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <div>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{m.business_name}</p>
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>{m.contact_name} · {m.phone}</p>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '10px', letterSpacing: '1px', backgroundColor: m.rep === 'Randy' ? '#e8f4f8' : '#f8e8f4', color: m.rep === 'Randy' ? '#0c5460' : '#4a2c7a', fontWeight: 'bold' }}>
                              {m.rep === 'Randy' ? 'R' : 'M'}
                            </span>
                            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '10px', letterSpacing: '1px', backgroundColor: '#f8f9fa', color: '#555' }}>
                              {m.meeting_type}
                            </span>
                            <select
                              value={m.status}
                              onChange={e => updateMeetingStatus(m.id, e.target.value)}
                              style={{ fontSize: '11px', padding: '4px 8px', border: '1px solid #d4c9bc', backgroundColor: bg, fontFamily: body, cursor: 'pointer' }}
                            >
                              <option value="scheduled">Scheduled</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                              <option value="no-show">No Show</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── LEADERBOARD TAB ───────────────────────────────────────────────── */}
        {activeTab === 'leaderboard' && (
          <div>
            <h2 style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: '0 0 8px' }}>Leaderboard</h2>
            <p style={{ color: '#888', fontSize: '13px', margin: '0 0 30px' }}>Based on contracts marked as Paid, Onboarding, or Live</p>

            {leaderboard.map((rep, i) => (
              <div key={i} style={{ border: '1px solid #d4c9bc', padding: '30px', marginBottom: '15px', backgroundColor: i === 0 ? 'white' : bg, position: 'relative', overflow: 'hidden' }}>
                {i === 0 && <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', backgroundColor: '#c9a882' }} />}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <p style={{ fontFamily: serif, fontSize: '48px', fontWeight: '700', color: i === 0 ? '#c9a882' : '#d4c9bc', margin: 0, lineHeight: 1 }}>#{i + 1}</p>
                    <div>
                      <p style={{ fontFamily: serif, fontSize: '22px', fontWeight: '700', color: dark, margin: '0 0 4px' }}>{rep.name}</p>
                      <p style={{ color: '#888', fontSize: '12px', letterSpacing: '2px', margin: 0 }}>{rep.role.toUpperCase()}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '40px', textAlign: 'right' }}>
                    <div>
                      <p style={{ color: red, fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>DEALS CLOSED</p>
                      <p style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: 0 }}>{rep.deals}</p>
                    </div>
                    <div>
                      <p style={{ color: red, fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>MRR</p>
                      <p style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: 0 }}>${rep.mrr}</p>
                    </div>
                    <div>
                      <p style={{ color: red, fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>SETUP FEES</p>
                      <p style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: 0 }}>${rep.setup}</p>
                    </div>
                  </div>
                </div>
                {rep.deals > 0 && (
                  <div style={{ marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '4px', overflow: 'hidden', height: '6px' }}>
                    <div style={{ height: '100%', backgroundColor: i === 0 ? '#c9a882' : red, width: `${Math.min(rep.deals * 10, 100)}%`, transition: 'width 0.5s ease' }} />
                  </div>
                )}
              </div>
            ))}

            <div style={{ border: '1px solid #d4c9bc', padding: '20px', marginTop: '20px', backgroundColor: 'white' }}>
              <p style={{ color: red, fontSize: '10px', letterSpacing: '3px', margin: '0 0 15px' }}>TEAM TOTALS</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div>
                  <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>COMBINED MRR</p>
                  <p style={{ fontFamily: serif, fontSize: '24px', fontWeight: '700', color: dark, margin: 0 }}>${leaderboard.reduce((s, r) => s + r.mrr, 0).toLocaleString()}</p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>TOTAL DEALS</p>
                  <p style={{ fontFamily: serif, fontSize: '24px', fontWeight: '700', color: dark, margin: 0 }}>{leaderboard.reduce((s, r) => s + r.deals, 0)}</p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>TOTAL SETUP FEES</p>
                  <p style={{ fontFamily: serif, fontSize: '24px', fontWeight: '700', color: dark, margin: 0 }}>${leaderboard.reduce((s, r) => s + r.setup, 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ANNOUNCEMENTS TAB ─────────────────────────────────────────────── */}
        {activeTab === 'announcements' && (
          <div>
            <h2 style={{ fontFamily: serif, fontSize: '32px', fontWeight: '700', color: dark, margin: '0 0 30px' }}>Announcements</h2>
            <div style={{ border: '1px solid #d4c9bc', padding: '30px', marginBottom: '30px' }}>
              <p style={{ color: red, fontSize: '10px', letterSpacing: '3px', margin: '0 0 20px' }}>POST ANNOUNCEMENT</p>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>TITLE</p>
                <input placeholder="e.g. New pricing update" value={announcementTitle} onChange={e => setAnnouncementTitle(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>MESSAGE</p>
                <textarea placeholder="Write your announcement here..." value={newAnnouncement} onChange={e => setNewAnnouncement(e.target.value)} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '12px', fontSize: '14px', fontFamily: body, color: '#2a1a1a', outline: 'none', width: '100%', resize: 'vertical', minHeight: '100px', boxSizing: 'border-box' }} />
              </div>
              <button onClick={handlePostAnnouncement} disabled={postingAnnouncement || !announcementTitle || !newAnnouncement} style={{ backgroundColor: postingAnnouncement ? '#ccc' : red, color: bg, padding: '12px 28px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                {postingAnnouncement ? 'POSTING...' : 'POST ANNOUNCEMENT'}
              </button>
            </div>
            {announcements.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', border: '1px solid #d4c9bc' }}>
                <p style={{ color: '#888', fontStyle: 'italic' }}>No announcements yet.</p>
              </div>
            ) : (
              announcements.map((a, i) => (
                <div key={i} style={{ border: '1px solid #d4c9bc', padding: '24px', marginBottom: '12px', backgroundColor: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <h3 style={{ fontFamily: serif, fontSize: '20px', fontWeight: '700', color: dark, margin: 0 }}>{a.title}</h3>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: '#888', fontSize: '11px', margin: 0 }}>{new Date(a.created_at).toLocaleDateString()}</p>
                      <p style={{ color: red, fontSize: '11px', letterSpacing: '1px', margin: '2px 0 0' }}>{a.posted_by}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', color: '#555', margin: 0, lineHeight: '1.8' }}>{a.content}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── TEAM TAB ──────────────────────────────────────────────────────── */}
        {activeTab === 'team' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
              <h2 style={{ fontFamily: serif, fontSize: '28px', fontWeight: '700', color: dark, margin: 0 }}>Team</h2>
              <button onClick={() => setShowAddMember(true)} style={{ backgroundColor: red, color: bg, padding: '10px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                + ADD MEMBER
              </button>
            </div>
            {showAddMember && (
              <div style={{ border: '1px solid #d4c9bc', padding: '32px', marginBottom: '32px', backgroundColor: 'white' }}>
                <p style={{ color: red, fontSize: '11px', letterSpacing: '3px', margin: '0 0 24px' }}>NEW MEMBER</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#888', margin: '0 0 8px' }}>FULL NAME</p>
                    <input value={newMember.full_name} onChange={e => setNewMember({...newMember, full_name: e.target.value})} placeholder="John Smith" style={{ width: '100%', padding: '12px', border: '0.5px solid #d4c9bc', backgroundColor: 'transparent', fontFamily: body, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#888', margin: '0 0 8px' }}>EMAIL</p>
                    <input value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} placeholder="john@hermespartnercapital.com" style={{ width: '100%', padding: '12px', border: '0.5px solid #d4c9bc', backgroundColor: 'transparent', fontFamily: body, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#888', margin: '0 0 8px' }}>TEMPORARY PASSWORD</p>
                    <input value={newMember.password} onChange={e => setNewMember({...newMember, password: e.target.value})} placeholder="TempPass123!" style={{ width: '100%', padding: '12px', border: '0.5px solid #d4c9bc', backgroundColor: 'transparent', fontFamily: body, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#888', margin: '0 0 8px' }}>ROLE</p>
                    <select value={newMember.role} onChange={e => setNewMember({...newMember, role: e.target.value})} style={{ width: '100%', padding: '12px', border: '0.5px solid #d4c9bc', backgroundColor: 'transparent', fontFamily: body, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}>
                      <option value="appointment_setter">Appointment Setter</option>
                      <option value="sales_rep">Sales Rep</option>
                      <option value="team_leader">Team Leader</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={handleAddMember} style={{ backgroundColor: red, color: bg, padding: '10px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body }}>
                    {addingMember ? 'ADDING...' : 'ADD MEMBER'}
                  </button>
                  <button onClick={() => setShowAddMember(false)} style={{ backgroundColor: 'transparent', border: '0.5px solid #d4c9bc', padding: '10px 24px', fontSize: '11px', letterSpacing: '2px', fontFamily: body, cursor: 'pointer', color: '#555' }}>
                    CANCEL
                  </button>
                </div>
              </div>
            )}
            {teamMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', border: '1px solid #d4c9bc' }}>
                <p style={{ color: '#888', fontStyle: 'italic' }}>No team members yet.</p>
              </div>
            ) : (
              teamMembers.map((m, i) => (
                <div key={i} style={{ border: '1px solid #d4c9bc', padding: '24px', marginBottom: '12px', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontFamily: serif, fontSize: '18px', fontWeight: '700', color: dark, margin: '0 0 4px' }}>{m.full_name}</p>
                    <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>{m.email}</p>
                  </div>
                  <select value={m.role} onChange={e => handleRoleChange(m.id, e.target.value)} style={{ padding: '8px 12px', border: '0.5px solid #d4c9bc', backgroundColor: 'transparent', fontFamily: body, fontSize: '11px', letterSpacing: '2px', outline: 'none', cursor: 'pointer' }}>
                    <option value="appointment_setter">APPOINTMENT SETTER</option>
                    <option value="sales_rep">SALES REP</option>
                    <option value="team_leader">TEAM LEADER</option>
                    <option value="admin">ADMIN</option>
                  </select>
                </div>
              ))
            )}
          </div>
        )}
      </div>

     {/* ── ONBOARDING PANEL ──────────────────────────────────────────────────── */}
      {onboardingOpen && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: '420px', height: '100vh', backgroundColor: dark, zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.3)', overflowY: 'auto' }}>
          
          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: '0.5px solid rgba(201,168,130,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: dark, zIndex: 10 }}>
            <div>
              <p style={{ color: '#c9a882', fontSize: '11px', letterSpacing: '3px', margin: '0 0 4px' }}>NEW CLIENT</p>
              <p style={{ color: '#f0ebe3', fontSize: '14px', margin: 0, fontFamily: serif }}>{onboardingForm.business_name || 'Onboarding'}</p>
            </div>
            <button onClick={() => setOnboardingOpen(false)} style={{ backgroundColor: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: '20px' }}>✕</button>
          </div>

          {/* Progress */}
          <div style={{ display: 'flex', gap: '4px', padding: '16px 24px 0' }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ flex: 1, height: '3px', backgroundColor: i <= onboardingStep ? '#c9a882' : 'rgba(201,168,130,0.2)', borderRadius: '2px' }} />
            ))}
          </div>

          <div style={{ padding: '24px', flex: 1 }}>

            {/* STEP 1 — ON THE PHONE: What you learned from the call */}
            {onboardingStep === 1 && (
              <div>
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '3px', margin: '0 0 4px' }}>STEP 1 — ON THE CALL</p>
                <p style={{ color: '#888', fontSize: '12px', margin: '0 0 24px' }}>Fill this out while on the phone with the client.</p>

                {/* Client Info */}
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '2px', margin: '0 0 16px', borderBottom: '0.5px solid rgba(201,168,130,0.2)', paddingBottom: '8px' }}>CLIENT INFO</p>
                <div style={obField}>
                  <label style={obLabel}>BUSINESS NAME</label>
                  <input style={obInput} value={onboardingForm.business_name} onChange={e => updateOB('business_name', e.target.value)} placeholder="e.g. Johnson Plumbing" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>CONTRACT ID</label>
                  <input style={obInput} value={onboardingForm.contract_id} onChange={e => updateOB('contract_id', e.target.value)} placeholder="e.g. HPC_1234567890" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>CLIENT EMAIL</label>
                  <input style={obInput} value={onboardingForm.client_email} onChange={e => updateOB('client_email', e.target.value)} placeholder="client@business.com" />
                </div>

                {/* Vapi Config */}
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '2px', margin: '24px 0 16px', borderBottom: '0.5px solid rgba(201,168,130,0.2)', paddingBottom: '8px' }}>VAPI CONFIG</p>
                <div style={obField}>
                  <label style={obLabel}>GREETING SCRIPT ("Thank you for calling ___!")</label>
                  <input style={obInput} value={onboardingForm.greeting_name} onChange={e => updateOB('greeting_name', e.target.value)} placeholder="e.g. Thank you for calling Johnson Plumbing!" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>AI NAME</label>
                  <input style={obInput} value={onboardingForm.ai_name || ''} onChange={e => updateOB('ai_name', e.target.value)} placeholder="e.g. Sarah, Alex" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>TONE</label>
                  <select style={obSelect} value={onboardingForm.tone} onChange={e => updateOB('tone', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="friendly_casual">Friendly and casual</option>
                    <option value="professional_formal">Professional and formal</option>
                    <option value="warm_reassuring">Warm and reassuring</option>
                    <option value="confident_direct">Confident and direct</option>
                  </select>
                </div>
                <div style={obField}>
                  <label style={obLabel}>TRANSFER NUMBER</label>
                  <input style={obInput} value={onboardingForm.transfer_number} onChange={e => updateOB('transfer_number', e.target.value)} placeholder="e.g. (224) 475-9646" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>EMERGENCY HANDLING</label>
                  <select style={obSelect} value={onboardingForm.emergency_handling} onChange={e => updateOB('emergency_handling', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="yes_24_7">Yes — 24/7</option>
                    <option value="yes_hours">Yes — business hours only</option>
                    <option value="sometimes">Sometimes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div style={obField}>
                  <label style={obLabel}>FALLBACK RESPONSE (if AI doesn't know something)</label>
                  <input style={obInput} value={onboardingForm.fallback || ''} onChange={e => updateOB('fallback', e.target.value)} placeholder="e.g. Let me have someone call you right back!" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>PRICING POLICY</label>
                  <select style={obSelect} value={onboardingForm.pricing_policy} onChange={e => updateOB('pricing_policy', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="no_phone">No prices over the phone</option>
                    <option value="flat_rates">Flat rates we can quote</option>
                    <option value="estimate_needed">In-person estimate only</option>
                  </select>
                </div>

                {/* GHL Setup */}
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '2px', margin: '24px 0 16px', borderBottom: '0.5px solid rgba(201,168,130,0.2)', paddingBottom: '8px' }}>GHL SETUP</p>
                <div style={obField}>
                  <label style={obLabel}>GHL SUB-ACCOUNT NAME</label>
                  <input style={obInput} value={onboardingForm.ghl_email} onChange={e => updateOB('ghl_email', e.target.value)} placeholder="e.g. Johnson Plumbing - Chicago" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>SMS ALERT NUMBER (who gets texted for new leads)</label>
                  <input style={obInput} value={onboardingForm.sms_alert_number || ''} onChange={e => updateOB('sms_alert_number', e.target.value)} placeholder="e.g. (224) 475-9646" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>THEIR PHONE PROVIDER (for call forwarding)</label>
                  <input style={obInput} value={onboardingForm.phone_provider || ''} onChange={e => updateOB('phone_provider', e.target.value)} placeholder="e.g. Verizon, AT&T, RingCentral" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>PIPELINE TO USE</label>
                  <input style={obInput} value={onboardingForm.pipeline || ''} onChange={e => updateOB('pipeline', e.target.value)} placeholder="e.g. New Leads, AI Receptionist Clients" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>TAGS TO APPLY TO NEW CONTACTS</label>
                  <input style={obInput} value={onboardingForm.ghl_tags || ''} onChange={e => updateOB('ghl_tags', e.target.value)} placeholder="e.g. AI Lead, Plumbing, Chicago" />
                </div>
                <div style={obField}>
                  <label style={obLabel}>ADDITIONAL NOTES</label>
                  <textarea rows={3} style={{ ...obInput, resize: 'none' }} value={onboardingForm.vapi_notes} onChange={e => updateOB('vapi_notes', e.target.value)} placeholder="Any other notes from the call..." />
                </div>

                <button onClick={() => setOnboardingStep(2)} style={{ backgroundColor: '#c9a882', color: dark, padding: '14px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, width: '100%', fontWeight: 'bold' }}>
                  CONTINUE TO SEND CLIENT FORM →
                </button>
              </div>
            )}

            {/* STEP 2 — SEND CLIENT THEIR FORM */}
            {onboardingStep === 2 && (
              <div>
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '3px', margin: '0 0 4px' }}>STEP 2 — SEND CLIENT FORM</p>
                <p style={{ color: '#888', fontSize: '12px', margin: '0 0 24px' }}>Still on the phone — explain the process, then hit send and hang up.</p>

                <div style={{ backgroundColor: 'rgba(201,168,130,0.1)', border: '0.5px solid rgba(201,168,130,0.3)', padding: '20px', borderRadius: '6px', marginBottom: '24px' }}>
                  <p style={{ color: '#c9a882', fontSize: '11px', letterSpacing: '2px', margin: '0 0 12px' }}>WHAT TO SAY ON THE PHONE</p>
                  <p style={{ color: '#f0ebe3', fontSize: '13px', lineHeight: '1.8', margin: 0 }}>
                    "Perfect — I'm going to send you a quick form right now to your email. It'll take about 5 minutes to fill out. Once we get that back, we'll start building your AI receptionist and have it live within 5 business days. You'll get an email with your login credentials once everything is ready. Any questions before I let you go?"
                  </p>
                </div>

                <div style={{ backgroundColor: 'rgba(201,168,130,0.05)', border: '0.5px solid rgba(201,168,130,0.2)', padding: '16px', borderRadius: '6px', marginBottom: '24px' }}>
                  <p style={{ color: '#888', fontSize: '11px', letterSpacing: '2px', margin: '0 0 8px' }}>SENDING TO</p>
                  <p style={{ color: '#f0ebe3', fontSize: '14px', margin: '0 0 4px', fontWeight: 'bold' }}>{onboardingForm.business_name}</p>
                  <p style={{ color: '#c9a882', fontSize: '13px', margin: 0 }}>{onboardingForm.client_email}</p>
                </div>

                <button onClick={handleSendOnboarding} disabled={sendingOnboarding} style={{ backgroundColor: sendingOnboarding ? '#555' : '#c9a882', color: dark, padding: '14px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: sendingOnboarding ? 'not-allowed' : 'pointer', fontFamily: body, width: '100%', fontWeight: 'bold', marginBottom: '12px' }}>
                  {sendingOnboarding ? 'SENDING...' : '📧 SEND FORM & HANG UP'}
                </button>

                <button onClick={() => setOnboardingStep(1)} style={{ backgroundColor: 'transparent', border: '0.5px solid rgba(201,168,130,0.3)', color: '#c9a882', padding: '12px 24px', fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', fontFamily: body, width: '100%' }}>
                  ← BACK
                </button>
              </div>
            )}

            {/* STEP 3 — BUILD & LAUNCH */}
            {onboardingStep === 3 && (
              <div>
                <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '3px', margin: '0 0 4px' }}>STEP 3 — BUILD & LAUNCH</p>
                <p style={{ color: '#888', fontSize: '12px', margin: '0 0 24px' }}>Off the phone — hit each button to build their setup automatically.</p>

                {/* CREATE VAPI ASSISTANT */}
                <div style={{ backgroundColor: onboardingForm.vapi_built ? 'rgba(40,167,69,0.15)' : 'rgba(201,168,130,0.05)', border: `0.5px solid ${onboardingForm.vapi_built ? 'rgba(40,167,69,0.3)' : 'rgba(201,168,130,0.2)'}`, borderRadius: '6px', padding: '16px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: onboardingForm.vapi_built ? '#28a745' : '#f0ebe3', fontSize: '13px', fontWeight: 'bold', margin: '0 0 2px' }}>
                        {onboardingForm.vapi_built ? '✓ Vapi Assistant Created' : 'Create Vapi Assistant'}
                      </p>
                      <p style={{ color: '#888', fontSize: '11px', margin: 0 }}>
                        {onboardingForm.vapi_assistant_id ? `ID: ${onboardingForm.vapi_assistant_id.slice(0, 8)}...` : 'AI receptionist built from your onboarding info'}
                      </p>
                    </div>
                    {!onboardingForm.vapi_built && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API}/api/vapi/create-assistant`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                business_name: onboardingForm.business_name,
                                ai_name: onboardingForm.ai_name || 'Alex',
                                tone: onboardingForm.tone,
                                greeting: onboardingForm.greeting_name,
                                transfer_number: onboardingForm.transfer_number,
                                emergency_handling: onboardingForm.emergency_handling,
                                fallback: onboardingForm.fallback,
                                services: onboardingForm.services,
                                hours: `Weekday: ${onboardingForm.hours_weekday}, Weekend: ${onboardingForm.hours_weekend}`
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              updateOB('vapi_built', true);
                              updateOB('vapi_assistant_id', data.assistantId);
                            } else {
                              alert('Error: ' + data.error);
                            }
                          } catch (err) {
                            alert('Error: ' + err.message);
                          }
                        }}
                        style={{ backgroundColor: '#c9a882', color: dark, padding: '8px 16px', fontSize: '10px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, fontWeight: '700', whiteSpace: 'nowrap' }}
                      >
                        CREATE →
                      </button>
                    )}
                  </div>
                </div>

{/* CREATE GHL SUBACCOUNT */}
                <div style={{ backgroundColor: onboardingForm.ghl_created ? 'rgba(40,167,69,0.15)' : 'rgba(201,168,130,0.05)', border: `0.5px solid ${onboardingForm.ghl_created ? 'rgba(40,167,69,0.3)' : 'rgba(201,168,130,0.2)'}`, borderRadius: '6px', padding: '16px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: onboardingForm.ghl_created ? '#28a745' : '#f0ebe3', fontSize: '13px', fontWeight: 'bold', margin: '0 0 2px' }}>
                        {onboardingForm.ghl_created ? '✓ GHL Subaccount Created' : 'Create GHL Subaccount'}
                      </p>
                      <p style={{ color: '#888', fontSize: '11px', margin: 0 }}>
                        {onboardingForm.ghl_location_id ? `Location ID: ${onboardingForm.ghl_location_id.slice(0, 8)}...` : 'Create their CRM and pipeline in GoHighLevel'}
                      </p>
                    </div>
                    {!onboardingForm.ghl_created && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API}/api/ghl/create-subaccount`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                business_name: onboardingForm.business_name,
                                email: onboardingForm.client_email,
                                phone: onboardingForm.sms_alert_number || '',
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              updateOB('ghl_created', true);
                              updateOB('ghl_location_id', data.locationId);
                              updateOB('ghl_api_key_generated', data.apiKey);
                            } else {
                              alert('Error: ' + data.error);
                            }
                          } catch (err) {
                            alert('Error: ' + err.message);
                          }
                        }}
                        style={{ backgroundColor: '#c9a882', color: dark, padding: '8px 16px', fontSize: '10px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, fontWeight: '700', whiteSpace: 'nowrap' }}
                      >
                        CREATE →
                      </button>
                    )}
                  </div>
                </div>

                {/* PROVISION CRM PORTAL */}
                <div style={{ backgroundColor: onboardingForm.portal_created ? 'rgba(40,167,69,0.15)' : 'rgba(201,168,130,0.05)', border: `0.5px solid ${onboardingForm.portal_created ? 'rgba(40,167,69,0.3)' : 'rgba(201,168,130,0.2)'}`, borderRadius: '6px', padding: '16px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: onboardingForm.portal_created ? '#28a745' : '#f0ebe3', fontSize: '13px', fontWeight: 'bold', margin: '0 0 2px' }}>
                        {onboardingForm.portal_created ? '✓ CRM Portal Created' : 'Provision CRM Portal'}
                      </p>
                      <p style={{ color: '#888', fontSize: '11px', margin: 0 }}>
                        {onboardingForm.portal_subdomain ? `${onboardingForm.portal_subdomain}.hermespartnercapital.co` : 'Create client dashboard and login'}
                      </p>
                    </div>
                    {!onboardingForm.portal_created && (
                      <button
                        onClick={async () => {
                          if (!onboardingForm.vapi_built) { alert('Create Vapi assistant first.'); return; }
                          const subdomain = onboardingForm.business_name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                          try {
                            const res = await fetch(`${API}/api/provision-client`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                business_name: onboardingForm.business_name,
                                subdomain,
                                owner_name: onboardingForm.business_name,
                                owner_email: onboardingForm.client_email,
                                owner_phone: onboardingForm.sms_alert_number || '',
                                vapi_assistant_id: onboardingForm.vapi_assistant_id,
                                ghl_location_id: onboardingForm.ghl_location_id || 'TZ0PdaPV7L5owPMWwZaw',
                                ghl_api_key: onboardingForm.ghl_api_key_generated || '',
                                created_by: 'Admin'
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              updateOB('portal_created', true);
                              updateOB('portal_subdomain', subdomain);
                              updateOB('portal_password', data.tempPassword);
                            } else {
                              alert('Error: ' + data.error);
                            }
                          } catch (err) {
                            alert('Error: ' + err.message);
                          }
                        }}
                        style={{ backgroundColor: '#c9a882', color: dark, padding: '8px 16px', fontSize: '10px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, fontWeight: '700', whiteSpace: 'nowrap' }}
                      >
                        CREATE →
                      </button>
                    )}
                  </div>
                </div>

                {/* SEND WELCOME EMAIL */}
                <div style={{ backgroundColor: onboardingForm.client_notified ? 'rgba(40,167,69,0.15)' : 'rgba(201,168,130,0.05)', border: `0.5px solid ${onboardingForm.client_notified ? 'rgba(40,167,69,0.3)' : 'rgba(201,168,130,0.2)'}`, borderRadius: '6px', padding: '16px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ color: onboardingForm.client_notified ? '#28a745' : '#f0ebe3', fontSize: '13px', fontWeight: 'bold', margin: '0 0 2px' }}>
                        {onboardingForm.client_notified ? '✓ Welcome Email Sent' : 'Send Welcome Email'}
                      </p>
                      <p style={{ color: '#888', fontSize: '11px', margin: 0 }}>Login credentials sent to {onboardingForm.client_email}</p>
                    </div>
                    {!onboardingForm.client_notified && (
                      <button
                        onClick={async () => {
                          if (!onboardingForm.portal_created) { alert('Provision portal first.'); return; }
                          updateOB('client_notified', true);
                          alert(`Welcome email was already sent automatically when the portal was provisioned to ${onboardingForm.client_email}`);
                        }}
                        style={{ backgroundColor: '#c9a882', color: dark, padding: '8px 16px', fontSize: '10px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, fontWeight: '700', whiteSpace: 'nowrap' }}
                      >
                        CONFIRM →
                      </button>
                    )}
                  </div>
                </div>

                {/* Credentials display */}
                {onboardingForm.portal_created && onboardingForm.portal_subdomain && (
                  <div style={{ backgroundColor: 'rgba(201,168,130,0.1)', border: '0.5px solid rgba(201,168,130,0.3)', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                    <p style={{ color: '#c9a882', fontSize: '10px', letterSpacing: '2px', margin: '0 0 10px' }}>CLIENT CREDENTIALS</p>
                    <p style={{ color: '#f0ebe3', fontSize: '12px', margin: '0 0 4px', fontFamily: 'monospace' }}>URL: {onboardingForm.portal_subdomain}.hermespartnercapital.co</p>
                    <p style={{ color: '#f0ebe3', fontSize: '12px', margin: '0 0 4px', fontFamily: 'monospace' }}>Email: {onboardingForm.client_email}</p>
                    <p style={{ color: '#f0ebe3', fontSize: '12px', margin: 0, fontFamily: 'monospace' }}>Password: {onboardingForm.portal_password}</p>
                  </div>
                )}

                <div style={{ marginTop: '16px' }}>
                  <button
                    onClick={async () => {
                      await supabase.from('onboarding').update({ status: 'live' }).eq('contract_id', onboardingForm.contract_id);
                      await fetch(`${API}/api/contracts/${onboardingForm.contract_id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'live' }) });
                      alert(`${onboardingForm.business_name} is now LIVE! 🦅`);
                      setOnboardingOpen(false);
                      setOnboardingStep(1);
                      fetchContracts();
                    }}
                    style={{ backgroundColor: '#28a745', color: 'white', padding: '14px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, width: '100%', fontWeight: 'bold' }}
                  >
                    🚀 MARK AS LIVE
                  </button>
                </div>
              </div>
            )}
            {onboardingStep === 4 && (
              <div>
                <p style={{ color: '#4DB87A', fontSize: '10px', letterSpacing: '3px', margin: '0 0 4px' }}>🟢 CLIENT IS LIVE</p>
                <p style={{ color: '#888', fontSize: '12px', margin: '0 0 24px' }}>This client is active. Here are their important details.</p>
                {[
                  { label: 'BUSINESS NAME', value: onboardingForm.business_name },
                  { label: 'CLIENT EMAIL', value: onboardingForm.client_email },
                  { label: 'CONTRACT ID', value: onboardingForm.contract_id },
                  { label: 'VAPI ASSISTANT ID', value: onboardingForm.vapi_assistant_id },
                  { label: 'CRM PORTAL URL', value: onboardingForm.portal_subdomain ? `${onboardingForm.portal_subdomain}.hermespartnercapital.co` : '—' },
                  { label: 'SUBDOMAIN', value: onboardingForm.portal_subdomain || '—' },
                ].map((item, i) => (
                  <div key={i} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '0.5px solid rgba(201,168,130,0.2)' }}>
                    <p style={{ color: '#888', fontSize: '10px', letterSpacing: '2px', margin: '0 0 4px' }}>{item.label}</p>
                    <p style={{ color: '#f0ebe3', fontSize: '13px', margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{item.value || '—'}</p>
                  </div>
                ))}
                <a
                  href={onboardingForm.portal_subdomain ? `https://${onboardingForm.portal_subdomain}.hermespartnercapital.co` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', backgroundColor: '#c9a882', color: dark, padding: '12px 24px', fontSize: '11px', letterSpacing: '2px', border: 'none', cursor: 'pointer', fontFamily: body, width: '100%', fontWeight: 'bold', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                >
                  OPEN CLIENT PORTAL →
                </a>
                <button
                  onClick={() => { setOnboardingStep(3); setOnboardingForm(prev => ({ ...prev, is_live: false })); }}
                  style={{ backgroundColor: 'transparent', border: '0.5px solid rgba(201,168,130,0.3)', color: '#c9a882', padding: '10px', fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', fontFamily: body, width: '100%' }}
                >
                  VIEW BUILD DETAILS
                </button>
              </div>
            )}
          </div>
          {/* Bottom nav between steps 2 and 3 */}
          {onboardingStep === 2 && (
            <div style={{ padding: '16px 24px', borderTop: '0.5px solid rgba(201,168,130,0.2)' }}>
              <button onClick={() => setOnboardingStep(3)} style={{ backgroundColor: 'transparent', border: '0.5px solid rgba(201,168,130,0.3)', color: '#c9a882', padding: '10px', fontSize: '11px', letterSpacing: '2px', cursor: 'pointer', fontFamily: body, width: '100%' }}>
                SKIP TO BUILD CHECKLIST →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Admin;